import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import NestableClickable from 'components/Common/NestableClickable'
import SearchTileRowInclusionItem from 'components/SearchKit/SearchTiles/shared/SearchTileRowInclusionItem'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import IconLabel from 'LuxKit/Label/IconLabel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { DrawerHotelTileProps, DrawerHotelInclusion, DrawerHotelPrice } from './types'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import useWarnMissingCurrency from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useWarnMissingCurrency'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import productBadgeMessages from 'i18n/productBadgeMessages'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import ModalContext from 'contexts/ModalContext'
import OfferInclusionsModal from '../OfferInclusionsModal'
import { getAccommodationUnavailableLabel } from 'tripPlanner/utils/accommodationPricingDisplay'

const INCLUSIONS_TRUNCATE_AFTER = 5
const AMENITIES_TRUNCATE_AFTER = 6

const TileContainer = styled(NestableClickable)`
  display: grid;
  grid-template-columns: 1fr;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(300)} 1fr;
  }
`

const ImageSection = styled.div`
  position: relative;
  height: ${rem(200)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    height: 100%;
    min-height: ${rem(250)};
  }
`

const TileStatusContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 1;
`

const TileProductBadgeContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-start: ${rem(12)};
  z-index: 1;
`

const ContentSection = styled(Group)`
  padding: ${rem(20)};
`

const TitleGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const PriceRow = styled.div`
  display: flex;
  justify-content: flex-end;

  ${mediaQueryUp.tablet} {
    margin-top: auto;
  }
`

const PriceActionColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(12)};
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(220)};
  }
`

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(4)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

interface InclusionItemProps {
  inclusion: DrawerHotelInclusion
}

function InclusionItem({ inclusion }: InclusionItemProps) {
  return (
    <OfferInclusionItem inclusion={inclusion} lineClamp={1} />
  )
}

function getHotelTilePriceLabel(
  intl: IntlShape,
  price: DrawerHotelPrice | undefined,
  isTotal: boolean | undefined,
  roomsText: string,
): string | undefined {
  if (!price) return undefined

  if (!price.nights) {
    return isTotal
      ? intl.formatMessage({
          id: 'nYa2Gp2Y_2QUGjc7LLVW',
          defaultMessage: 'Total',
          description: 'Hotel tile price caption for a selected-rate total when stay length is unavailable in the trip planner AI chat',
        })
      : intl.formatMessage({
          id: 'Hnb6gH7u8FL_sn8xUpLE',
          defaultMessage: 'From',
          description: 'Hotel tile price caption when stay length is unavailable in the trip planner AI chat',
        })
  }

  return isTotal
    ? intl.formatMessage(
        {
          id: 'z4Jpopbt0zXu7ipWSDDT',
          defaultMessage: '{count, plural, one {# night} other {# nights}} total',
          description: 'Hotel tile price caption showing a selected-rate total in the trip planner AI chat',
        },
        { count: price.nights },
      )
    : intl.formatMessage(
        {
          id: 'jWQ3t1bWlJ_Vvv1o6Zhs',
          defaultMessage: '{nights, plural, one {# night} other {# nights}}, {roomsText} from',
          description: 'Duration with rooms for offer tile pricing on the offer page',
        },
        { nights: price.nights, roomsText },
      )
}

function LimitedTimeExclusiveBadge() {
  const intl = useIntl()

  return (
    <TileProductBadgeContainer>
      <IconLabel
        aria-label={intl.formatMessage(productBadgeMessages.limitedTimeLuxExclusiveLe)}
        kind="primary"
        variant="exclusive"
        icon={<SolidStarCircleIcon />}
      />
    </TileProductBadgeContainer>
  )
}

function DrawerHotelTile({
  hotel,
  isBooked = false,
  onSelectHotel,
  position,
  trackingOffer,
  itineraryData,
  contextStop,
  onSendMessage,
  onAfterAction,
  onShowOnMap,
  filterNights,
  roomCount = 1,
}: DrawerHotelTileProps) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const showModal = useContext(ModalContext)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const hasFiredImpressionRef = useRef(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const {
    name,
    location,
    images,
    rating,
    inclusions,
    tileAmenities,
    price,
    isTotal,
    includesTaxesAndFees,
    unavailableReason,
    luxPlusPrice,
    deeplinkUrl,
    mapUrl,
    value,
    discountPercent,
    memberDiscountPercent,
  } = hotel
  const shouldShowLimitedTimeExclusiveBadge = hotel.offerType === OFFER_TYPE_HOTEL
  const offerConfig = trackingOffer && 'luxPlus' in trackingOffer
    ? trackingOffer.luxPlus
    : undefined
  const isLuxPlusOffer = !!offerConfig?.access
  const isLuxPlusMemberOnlyOffer = offerConfig?.access === 'memberOnly'

  useEffect(() => {
    if (!inView || !trackingOffer || position === undefined || hasFiredImpressionRef.current) {
      return
    }

    hasFiredImpressionRef.current = true

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: trackingOffer,
      position,
    })
  }, [dispatchOfferListEvent, inView, position, trackingOffer])

  const missingCurrencyContext = useMemo(() => ({ hotelId: hotel?.id }), [hotel?.id])
  useWarnMissingCurrency(
    Boolean(price && !price.currency),
    '[TripChat] Hotel price missing currency',
    missingCurrencyContext,
  )

  const handleSelectHotel = useCallback(() => {
    if (trackingOffer && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: trackingOffer,
        position,
      })
    }

    onSelectHotel(hotel)
  }, [dispatchOfferListEvent, hotel, onSelectHotel, position, trackingOffer])

  const handleShowOnMapClick = useCallback(() => {
    onShowOnMap?.(hotel)
  }, [onShowOnMap, hotel])

  const visibleInclusions = useMemo(
    () => (inclusions ?? []).slice(0, INCLUSIONS_TRUNCATE_AFTER),
    [inclusions],
  )
  const hiddenInclusionsCount = Math.max(0, (inclusions ?? []).length - INCLUSIONS_TRUNCATE_AFTER)
  const handleShowMoreInclusions = useCallback(() => {
    showModal(
      <OfferInclusionsModal
        offerName={name}
        inclusions={inclusions ?? []}
        onViewOffer={handleSelectHotel}
      />,
    )
  }, [handleSelectHotel, inclusions, name, showModal])
  const visibleAmenities = useMemo(
    () => (tileAmenities ?? []).slice(0, AMENITIES_TRUNCATE_AFTER),
    [tileAmenities],
  )
  const shouldShowMapLink = !!mapUrl && !!onShowOnMap
  const roomsText = intl.formatMessage(
    {
      id: 'wNIAQxc_tHFdfW1RYsY6',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count for offer tile pricing on the hotel or room booking page',
    },
    { count: Math.max(1, roomCount) },
  )
  const priceLabel = getHotelTilePriceLabel(intl, price, isTotal, roomsText)
  const unavailableLabel = unavailableReason
    ? getAccommodationUnavailableLabel(unavailableReason, intl)
    : undefined
  const effectiveDiscountPercent = luxPlusPrice && price && luxPlusPrice < price.total && canViewLuxPlusBenefits && memberDiscountPercent != null
    ? memberDiscountPercent
    : discountPercent
  const shouldDisplayValue = trackingOffer && 'lowestPricePackage' in trackingOffer
    ? trackingOffer.lowestPricePackage?.shouldDisplayValue ?? true
    : true
  const isDiscountPillHidden = trackingOffer && 'isDiscountPillHidden' in trackingOffer
    ? trackingOffer.isDiscountPillHidden
    : false
  const { shouldShowValue, shouldShowDiscount } = getOfferValueDisplayVisibility({
    value,
    discountPercent: effectiveDiscountPercent,
    shouldDisplayValue,
    isDiscountPillHidden,
  })

  return (
    <TileContainer ref={inViewRef} data-testid="drawer-hotel-tile" onClick={handleSelectHotel}>
      <ImageSection>
        {shouldShowLimitedTimeExclusiveBadge && <LimitedTimeExclusiveBadge />}
        {isBooked && (
          <TileStatusContainer>
            <BookedLabel />
          </TileStatusContainer>
        )}
        {onSendMessage && !isBooked && (
          <TileStatusContainer>
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={name}
              offerId={hotel.offerId}
              offerType={hotel.offerType}
              hotelCity={location}
              hotelCoordinates={hotel.geoLocation}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              onAfterAction={onAfterAction}
              filterNights={filterNights}
            />
          </TileStatusContainer>
        )}
        <ProductPaletteProvider palette={isLuxPlusOffer ? 'lux-plus' : 'default'}>
          <ImageCarousel
            images={images}
            arrowBehaviour="interaction-visible"
            arrowStyle="dark"
          />
        </ProductPaletteProvider>
      </ImageSection>

      <ContentSection direction="vertical" gap={16}>
        <Group
          direction="horizontal"
          gap={12}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <TitleGroup direction="vertical" gap={4}>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="small" colour="light-foreground-alt">
                {location}
              </BodyText>
              {shouldShowMapLink && (
                <>
                  <BodyText variant="small" colour="light-foreground-alt">{intl.formatMessage(middleDotSeparatorMessage)}</BodyText>
                  <TextLink size="small" onClick={handleShowOnMapClick}>
                    {intl.formatMessage({
                      id: 'cjO2hzRshkBMda0Hg20K',
                      defaultMessage: 'Show on map',
                      description: 'Link to show hotel location on map on the hotel or room booking page',
                    })}
                  </TextLink>
                </>
              )}
            </Group>
            <Heading variant="heading5" lineClamp={2}>
              {name}
            </Heading>
            {isLuxPlusMemberOnlyOffer && <LuxPlusLabel type="member-only-offer" />}
          </TitleGroup>

          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              onlyNumber
              variant="small"
              squareContainer
            />
          )}
        </Group>

        {visibleInclusions.length > 0 && (
          <Group direction="vertical" gap={4}>
            {visibleInclusions.map((inclusion) => (
              <InclusionItem key={inclusion.id} inclusion={inclusion} />
            ))}
            {hiddenInclusionsCount > 0 && (
              <TextLink size="medium" onClick={handleShowMoreInclusions}>
                {intl.formatMessage(
                  {
                    id: 'T7vrcmV7MnW1J_G47i0o',
                    defaultMessage: '+{count} more',
                    description: 'Link showing count of additional hidden hotel inclusions',
                  },
                  { count: hiddenInclusionsCount },
                )}
              </TextLink>
            )}
          </Group>
        )}
        {visibleInclusions.length === 0 && visibleAmenities.length > 0 && (
          <AmenitiesGrid>
            {visibleAmenities.map((amenity) => (
              <SearchTileRowInclusionItem
                key={amenity.id}
                inclusion={amenity}
                lineClamp={1}
              />
            ))}
          </AmenitiesGrid>
        )}

        <PriceRow>
          <PriceActionColumn data-testid="drawer-hotel-price-action">
            {!!price && priceLabel && price.total !== undefined && (
              <PriceDetails
                price={price.total}
                luxPlusPrice={luxPlusPrice}
                currencyCode={price.currency ?? currentCurrency}
                priceLabel={priceLabel}
                totalLabel="total"
                value={shouldShowValue ? value : undefined}
                discount={shouldShowDiscount ? effectiveDiscountPercent : undefined}
                discountDisplayType="following"
                showTaxesAndFees={includesTaxesAndFees ?? false}
                offerConfig={offerConfig}
                showValueWithoutDiscount
              />
            )}
            {!price && unavailableLabel && (
              <BodyText variant="large" weight="bold">
                {unavailableLabel}
              </BodyText>
            )}
            {!price && !unavailableLabel && !!deeplinkUrl && (
              <TextLink
                size="medium"
                href={deeplinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<LineExternalLinkAltIcon />}
              >
                {intl.formatMessage({
                  id: 'TM5M7ZAPmTq1YXDuSEVJ',
                  defaultMessage: 'Pricing',
                  description: 'Link to view hotel pricing details on the hotel or room booking page',
                })}
              </TextLink>
            )}

            <ProductPaletteProvider palette={isLuxPlusOffer ? 'lux-plus' : 'default'}>
              <TextButton
                kind="primary"
                size="medium"
                fit="flex"
                onClick={handleSelectHotel}
              >
                {intl.formatMessage({
                  id: '0cKWSwyDZvKU0-9U6UrT',
                  defaultMessage: 'View offer',
                  description: 'Button to view hotel offer details on the hotel or room booking page',
                })}
              </TextButton>
            </ProductPaletteProvider>
          </PriceActionColumn>
        </PriceRow>
      </ContentSection>
    </TileContainer>
  )
}

export default memo(DrawerHotelTile)
